// İSG uzmanı portalı — Şube oluştur/düzenle formu (Modal + RHF + Zod). api-contract
// §10.2: `{ name, code?, address?, phone? }`. Firmaya kapsanmış (CompanyId token'dan;
// istemci göndermez). Başarıda liste invalidasyonu + bildirim (onSuccess).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  type BranchSettingsDto,
  Button,
  Field,
  Input,
  Modal,
  Spinner,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { settingsKeys } from '../../lib/settingsKeys';

interface BranchFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  branch?: BranchSettingsDto | null;
  onSuccess?: () => void;
}

function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function BranchFormModal({ open, onOpenChange, branch, onSuccess }: BranchFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(branch);

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('settings.validation.nameRequired')),
        code: z.string().trim().optional(),
        address: z.string().trim().optional(),
        phone: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: branch?.name ?? '',
      code: branch?.code ?? '',
      address: branch?.address ?? '',
      phone: branch?.phone ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const body = {
        name: values.name.trim(),
        code: nullable(values.code),
        address: nullable(values.address),
        phone: nullable(values.phone),
      };
      return branch ? settingsApi.updateBranch(branch.uuid, body) : settingsApi.createBranch(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.branches() });
      // Şube seçenekleri (saha formu) da tazelensin.
      queryClient.invalidateQueries({ queryKey: settingsKeys.branchOptions() });
      reset();
      onSuccess?.();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('settings.branches.edit.title') : t('settings.branches.create.title')}
      closeLabel={t('settings.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('settings.common.cancel')}
          </Button>
          <Button type="submit" form="branch-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('settings.common.save')} />
            ) : isEdit ? (
              t('settings.common.save')
            ) : (
              t('settings.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="branch-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="branch-name"
          label={t('settings.branches.field.name')}
          error={formState.errors.name?.message}
          required
        >
          <Input id="branch-name" invalid={Boolean(formState.errors.name)} {...register('name')} />
        </Field>

        <Field htmlFor="branch-code" label={t('settings.branches.field.code')}>
          <Input id="branch-code" {...register('code')} />
        </Field>

        <Field htmlFor="branch-address" label={t('settings.branches.field.address')}>
          <Input id="branch-address" {...register('address')} />
        </Field>

        <Field htmlFor="branch-phone" label={t('settings.branches.field.phone')}>
          <Input id="branch-phone" type="tel" {...register('phone')} />
        </Field>
      </form>
    </Modal>
  );
}
